/**
 * Quão velho está o dado publicado — funções puras (testadas em
 * `frescor.test.ts`).
 *
 * O pipeline publica a cada 15 minutos, mas o agendamento do GitHub Actions
 * não é garantido: o agendamento original, de 10 em 10 minutos, não disparou
 * uma única vez em quase duas horas.
 *
 * Dado velho servido em silêncio é pior que dado ausente — a pessoa lê a chuva
 * de ontem achando que é a de agora. Por isso o atraso é medido e mostrado, no
 * painel e como ponto amarelo no trilho.
 */

/** Cadência nominal do pipeline (ver `.github/workflows/dados.yml`). */
export const CADENCIA_MIN = 15;

/** Três ciclos perdidos: deixa de ser atraso normal e vira problema. */
export const ATRASO_AVISO_MIN = CADENCIA_MIN * 3;

/** Meio dia de silêncio: a grade de 24 h já perdeu metade da validade. */
export const ATRASO_GRAVE_MIN = 12 * 60;

export type Frescor = 'ok' | 'atrasado' | 'velho' | 'desconhecido';

/** Minutos desde a publicação; `null` quando não dá para saber. */
export function atrasoMin(gerado: number | null, agora: number = Date.now()): number | null {
  if (gerado === null || !Number.isFinite(gerado)) return null;
  // Relógio do visitante adiantado não deve virar "atraso negativo".
  return Math.max(0, (agora - gerado) / 60_000);
}

export function frescorDe(gerado: number | null, agora: number = Date.now()): Frescor {
  const min = atrasoMin(gerado, agora);
  if (min === null) return 'desconhecido';
  if (min >= ATRASO_GRAVE_MIN) return 'velho';
  if (min >= ATRASO_AVISO_MIN) return 'atrasado';
  return 'ok';
}

/** Texto do aviso, ou `null` quando está tudo em dia. */
export function avisoDeFrescor(gerado: number | null, agora: number = Date.now()): string | null {
  const f = frescorDe(gerado, agora);
  if (f === 'ok' || f === 'desconhecido') return null;
  const min = atrasoMin(gerado, agora)!;
  const quanto = min >= 120 ? `${Math.floor(min / 60)} h` : `${Math.round(min)} min`;
  return f === 'velho'
    ? `Dados parados há ${quanto}. O que está no mapa não é a chuva de agora.`
    : `Dados sem atualizar há ${quanto} — o normal é a cada ${CADENCIA_MIN} min.`;
}
